// src/components/PolicyEvidenceBadges.jsx
// Reusable component for displaying policy directive evidence on controls

import React, { useState } from 'react';
import {
  Badge,
  Group,
  Stack,
  Paper,
  Text,
  Tooltip,
  Collapse,
  ActionIcon
} from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';

/**
 * Display policy evidence badges for a control
 * @param {Array} policyEvidence - Array of policy directive matches
 * @param {boolean} compact - Show compact view (default: true)
 * @param {boolean} expandable - Allow expanding to show details (default: false)
 */
export default function PolicyEvidenceBadges({ policyEvidence, compact = true, expandable = false }) {
  const [expanded, setExpanded] = useState(false);

  if (!policyEvidence || policyEvidence.length === 0) {
    return (
      <Badge color="gray" size="xs" variant="light">
        ⚠️ No policy match
      </Badge>
    );
  }

  // Compact view: show badges with tooltips
  if (compact && !expanded) {
    return (
      <Group gap={4} align="center">
        {policyEvidence.slice(0, 2).map((directive, idx) => (
          <Tooltip
            key={idx}
            label={
              <div style={{ maxWidth: 300 }}>
                <Text size="xs" fw={600}>{directive.directiveName}</Text>
                <Text size="xs" c="dimmed">Section {directive.sectionNumber}</Text>
                <Text size="xs" mt={4}>{directive.text?.slice(0, 150)}...</Text>
              </div>
            }
            multiline
            withArrow
          >
            <Badge size="xs" color="blue" variant="light">
              🎯 {directive.directiveName.slice(0, 20)}{directive.directiveName.length > 20 ? '...' : ''} ({Math.round(directive.similarity * 100)}%)
            </Badge>
          </Tooltip>
        ))}
        {policyEvidence.length > 2 && (
          <Badge size="xs" color="gray" variant="light">
            +{policyEvidence.length - 2} more
          </Badge>
        )}
        {expandable && (
          <ActionIcon
            size="xs"
            variant="subtle"
            onClick={() => setExpanded(!expanded)}
            title={expanded ? "Collapse details" : "View details"}
          >
            {expanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
          </ActionIcon>
        )}
      </Group>
    );
  }

  // Expanded view: show full directive details
  return (
    <Stack gap={8}>
      {!compact && expandable && (
        <Group>
          <Text size="sm" fw={600}>🏷️ Policy Evidence ({policyEvidence.length})</Text>
          <ActionIcon
            size="sm"
            variant="subtle"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
          </ActionIcon>
        </Group>
      )}

      <Collapse in={!compact || expanded}>
        <Stack gap={8}>
          {policyEvidence.map((directive, idx) => (
            <Paper key={idx} p="sm" withBorder radius="md">
              <Group justify="space-between" mb={4}>
                <Text size="sm" fw={600}>
                  {idx === 0 && '🥇'} {idx === 1 && '🥈'} {idx === 2 && '🥉'}
                  {idx > 2 && '🎯'} {directive.directiveName}
                </Text>
                <Badge color="blue" size="sm">
                  {Math.round(directive.similarity * 100)}% match
                </Badge>
              </Group>

              <Group gap={8} mb={4}>
                <Text size="xs" c="dimmed">
                  📄 {directive.file_name}
                </Text>
                <Text size="xs" c="dimmed">
                  Section: {directive.sectionNumber}
                </Text>
                {directive.controlType && directive.controlType !== 'N/A' && (
                  <Badge size="xs" variant="outline">
                    {directive.controlType}
                  </Badge>
                )}
              </Group>

              {directive.sectionTitle && directive.sectionTitle !== 'N/A' && (
                <Text size="xs" c="dimmed" mb={4}>
                  {directive.sectionTitle}
                </Text>
              )}

              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {directive.text}
              </Text>

              {directive.regulatoryReference && directive.regulatoryReference !== 'N/A' && (
                <Text size="xs" c="dimmed" mt={8} style={{ fontStyle: 'italic' }}>
                  Regulatory Ref: {directive.regulatoryReference}
                </Text>
              )}
            </Paper>
          ))}
        </Stack>
      </Collapse>
    </Stack>
  );
}
